import { Component, OnInit, inject } from '@angular/core';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {WeatherService} from '../../../../core/services/weather.service';
import {AsyncPipe, UpperCasePipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {TemperaturePipe} from '../../pipes/temperature.pipe';
import {TimePipe} from '../../../../shared/pipes/time.pipe';
import {PrecipitationPipe} from '../../pipes/precipitation.pipe';
import {WindPipe} from '../../pipes/wind.pipe';
import {TextWithIconComponent} from '../../../../shared/components/text-with-icon/text-with-icon.component';
import {RelativeDatePipe} from '../../../../shared/pipes/relative-date.pipe';
import {Weather} from '@birco/rainmesh-api';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {ChartTemperatureComponent} from '../../components/chart-temperature/chart-temperature.component';
import {ChartPrecipitationComponent} from '../../components/chart-precipitation/chart-precipitation.component';
import {combineLatest, map} from 'rxjs';
import {selectDeviceById} from "../../../devices/state/device.selectors";
import {Store} from "@ngrx/store";
import {DeviceActions} from "../../../devices/state/device.actions";
import {LocalizedDatePipe} from '../../../../shared/pipes/localized-date.pipe';
import {LocalizedPercentPipe} from '../../../../shared/pipes/localized-percent.pipe';

@Component({
  selector: 'app-page-weather',
  imports: [
    CardComponent,
    AsyncPipe,
    TranslatePipe,
    LocalizedPercentPipe,
    TemperaturePipe,
    TimePipe,
    LocalizedDatePipe,
    PrecipitationPipe,
    WindPipe,
    TextWithIconComponent,
    RelativeDatePipe,
    UpperCasePipe,
    NgxChartsModule,
    ChartTemperatureComponent,
    ChartPrecipitationComponent,
  ],
  templateUrl: './page-weather.component.html',
  styleUrl: './page-weather.component.scss'
})
export class PageWeatherComponent implements OnInit {
  private _weatherService = inject(WeatherService);
  private store = inject(Store);

  loading$ = this._weatherService.loading$();
  hourlyForecast$ = this._weatherService.hourlyForecast$()
  weather$ = combineLatest([
    this._weatherService.location$(),
    this._weatherService.currentWeather$(),
    this._weatherService.todayForecast$(),
    this._weatherService.extendedForecast$(),
    this.store.select(selectDeviceById("76ef4018-5e83-4915-b35a-9449b3869963")),
  ]).pipe(
    map(([location, now, today, extendedForecast, weatherStation]) => {
      if (location && now && today && extendedForecast) {
        return {
          location,
          now,
          today,
          extendedForecast,
          weatherStation
        }
      } else {
        return null
      }
    })
  );

  ngOnInit() {
    this.store.dispatch(DeviceActions.public.loadDeviceById({
      id: "76ef4018-5e83-4915-b35a-9449b3869963",
    }));
  }

  getRainHint(weather: Weather): { error: boolean, text: string } | undefined {
    return this._weatherService.utils.getRainHint(weather)
  }

  getWeatherIcon(weather: Weather): string {
    return this._weatherService.utils.getWeatherIcon(weather)
  }

  getGeoInfo(lat: number, lon: number): string {
    return this._weatherService.utils.getGeoInfo(lat, lon)
  }

  getWeatherStationTemperature(measurement: string): number | null {
    return this.getWeatherStationValue(measurement, 'AIR_TEMPERATURE');
  }

  getWeatherStationRainGauge(measurement: string): number | null {
    return this.getWeatherStationValue(measurement, 'RAIN_GAUGE');
  }

  getWeatherStationAirHumidity(measurement: string): number | null {
    const value = this.getWeatherStationValue(measurement, 'AIR_HUMIDITY');
    return value === null ? null : value / 100;
  }

  private getWeatherStationValue(measurement: string, labelKey: string): number | null {
    try {
      const weatherData = JSON.parse(measurement);
      if (!Array.isArray(weatherData)) {
        return null;
      }

      const value = weatherData.find((item: any) => item?.labelKey === labelKey)?.value;
      const parsedValue = Number(value);

      return Number.isFinite(parsedValue) ? parsedValue : null;
    } catch {
      return null;
    }
  }
}
